/**
 * Frontend Auth Service
 * =====================
 * Replaces Firebase Auth SDK with fetch() calls to local API endpoints.
 * Stores JWT in localStorage for session persistence.
 */

const TOKEN_KEY = 'rv_token';

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function login(email, password) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Login failed');
    err.code = res.status === 429 ? 'auth/too-many-requests' : 'auth/invalid-credential';
    throw err;
  }
  setToken(data.token);
  return data.user;
}

export async function forgotPassword(email) {
  const res = await fetch('/api/auth/forgot-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Failed to send password reset email');
    err.code = res.status === 503 ? 'auth/reset-not-configured' : 'auth/reset-failed';
    throw err;
  }
  return data;
}

export async function signup(email, password, displayName) {
  const res = await fetch('/api/auth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, displayName }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Signup failed');
    if (res.status === 409) err.code = 'auth/email-already-in-use';
    throw err;
  }
  setToken(data.token);
  return data.user;
}

export async function getCurrentUser() {
  const token = getToken();
  if (!token) return null;

  try {
    const res = await fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      clearToken();
      return null;
    }
    return await res.json();
  } catch {
    clearToken();
    return null;
  }
}

export function logout() {
  clearToken();
}

export async function updateProfile(profileData) {
  const res = await fetch('/api/auth/profile', {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(profileData),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update profile');
  return data;
}

export async function uploadProfileImage(file) {
  if (file.size > 2 * 1024 * 1024) {
    throw new Error('Image must be under 2MB');
  }
  const allowed = ['image/jpeg', 'image/png', 'image/webp'];
  if (!allowed.includes(file.type)) {
    throw new Error('Only JPEG, PNG, and WebP images are allowed');
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await fetch('/api/auth/profile-image', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ image: reader.result }),
        });
        const data = await res.json();
        if (!res.ok) reject(new Error(data.error || 'Upload failed'));
        else resolve(data);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export async function deleteProfileImage() {
  const res = await fetch('/api/auth/profile-image', {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete image');
  return data;
}

export async function changePassword(currentPassword, newPassword) {
  const res = await fetch('/api/auth/password', {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to change password');
  return data;
}
